/* eslint-disable @typescript-eslint/no-empty-function */
import { IconName } from '@hubtype/ui-react-web'
import cloneDeep from 'lodash.clonedeep'
import { Edge, XYPosition } from 'reactflow'
import { v7 as uuidv7 } from 'uuid'

import { MAIN_FLOW } from '../../../UI/constants'
import { NonMessageContents } from '../../../UI/types'
import { HtBaseNode } from '../cms/hubtype/common'
import { LocaleCode } from '../locales/code'
import { ContentId } from './common'
import { ContentType } from './content-types'

export interface NodeErrors {
  showErrors?: boolean
  hasDuplicatedIdentifier?: boolean
  duplicatedIdentifierError: string
  fieldErrors: string[]
  localesWithErrors: string[]
}

export abstract class ContentFieldsBase {
  // react-flow library requires ids for all nodes. Before a new content is added to CMS, we create a temporary id
  protected constructor(public id = uuidv7()) {}
  abstract contentType(): ContentType

  getContentId(): ContentId {
    return { id: this.id, type: this.contentType() }
  }

  fieldsIncludeString = (
    fields: (string | undefined)[],
    value: string
  ): boolean => {
    const includesString = fields.map(f => {
      if (f && typeof f === 'string' && f.toLowerCase().includes(value)) {
        return true
      }
      return false
    })
    return includesString.indexOf(true) !== -1
  }
}

export abstract class TopContentFieldsBase extends ContentFieldsBase {
  public edges: Edge[] = []
  public errors: NodeErrors
  constructor(
    public code = '',
    public flowId = MAIN_FLOW.id,
    public isCodeAiGenerated = false,
    public isMeaningful = false
  ) {
    super()
    this.errors = {
      duplicatedIdentifierError: 'Content ID is duplicated',
      fieldErrors: [],
      localesWithErrors: [],
    }
  }

  abstract hasString(value: string): boolean
  abstract getConnections(edges: Edge[]): any

  setValuesByLocale(
    locale: string,
    nonMessageContents: NonMessageContents | undefined
  ): void {}
  addLocale(newLocale: LocaleCode, copyFrom: LocaleCode): void {}
  removeLocales(localesToRemove: LocaleCode[]): void {}
  setContentByLocale(locale: LocaleCode): void {}

  protected getMissingFieldsErrors(fields: string[]): string[] {
    return fields.map(field => `${field} is missing`)
  }

  protected getTooLongFieldsErrors(fields: string[]): string[] {
    return fields.map(field => `${field} is too long`)
  }

  protected aggregateErrors(errors: string[]): string[] {
    const errorCount = new Map<string, number>()

    errors.forEach(error => {
      errorCount.set(error, (errorCount.get(error) || 0) + 1)
    })

    return Array.from(errorCount.entries()).map(([error, count]) => {
      if (count === 1) return error
      const mainPart = error.replace(' is missing', '')
      return `${count} ${mainPart.toLowerCase()}s are missing`
    })
  }

  protected hasEdgeWithHandle(handle: string): boolean {
    return this.edges.some(edge => edge.sourceHandle?.includes(handle))
  }

  getLocalesWithErrors(locales: LocaleCode[]): string[] {
    this.errors.localesWithErrors = []
    return []
  }

  getFollowUp = (edges: any[]): any | undefined => {
    return edges.find(e => e.sourceHandle === 'follow_up_' + this.id)
  }

  static setHubtypeCmsCommonData(
    newNode: TopContentFieldsBase,
    htNode: HtBaseNode
  ) {
    newNode.id = htNode.id
    newNode.code = htNode.code
    newNode.isCodeAiGenerated = htNode.is_code_ai_generated || false
    newNode.flowId = htNode.flow_id || MAIN_FLOW.id
    newNode.isMeaningful = htNode.is_meaningful || false
    newNode.errors.showErrors = true
  }

  getHubtypeCmsCommonData(position: XYPosition): HtBaseNode {
    return {
      id: this.id,
      code: this.code,
      is_code_ai_generated: this.isCodeAiGenerated,
      is_meaningful: this.isMeaningful,
      flow_id: this.flowId,
      meta: position,
      follow_up: undefined,
      target: undefined,
    }
  }

  static copyContent<T extends TopContentFieldsBase>(
    content: T,
    newContent: T
  ): T {
    const clonedContent = cloneDeep(content)
    clonedContent.id = newContent.id
    Object.assign(newContent, clonedContent)
    return newContent
  }

  getConditionalIdMappings = <T extends { id: string }>(
    newItems: T[],
    oldItems: T[],
    oldId: string
  ) => {
    const itemsMappings = oldItems.reduce(
      (acc, oldItem, index) => {
        if (newItems[index]) {
          const key = `${oldId}-${oldItem.id}`
          const value = `${this.id}-${newItems[index].id}`
          acc[key] = value
        }
        return acc
      },
      {} as { [key: string]: string }
    )
    const defaultKey = `${oldId}-default`
    const defaultValue = `${this.id}-default`
    const defaultMapping = { [defaultKey]: defaultValue }
    return Object.assign({}, itemsMappings, defaultMapping)
  }

  getConditionalConnections = <T extends { id: string }>(
    items: T[],
    edges: Edge[]
  ) => {
    const itemsEdges = items.map(item =>
      edges.find(edge => edge.sourceHandle === this.id + '-' + item.id)
    )
    const defaultEdge = edges.find(
      edge => edge.sourceHandle === this.id + '-default'
    )
    return {
      buttons: [...itemsEdges, defaultEdge],
      followUp: undefined,
    }
  }

  setRandomContentId(): void {
    if (!this.code) {
      this.isCodeAiGenerated = true
      const suffix = uuidv7().slice(-3)
      this.code = this.contentType().toUpperCase() + '_' + suffix
    }
  }

  getDisplayInfo(): { name: string; icon: IconName } {
    return { name: this.contentType(), icon: '' }
  }

  getIdentifier(): string {
    return this.code
  }
}
